import { Check as CheckIcon, Timer as TimerIcon } from '@mui/icons-material'
import { Button, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { addMinutes, differenceInMinutes, isEqual, setHours, setMinutes, startOfDay } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { ANY_RESOURCE_ID } from '../../../../../../../../constants'
import { useFormat, useIsAnyResource, useLocale } from 'hooks'
import { theme } from '../../../../../../../../modules/App/Providers/Theme/theme'
import { AppointmentFormData, ResourceAvailability } from 'types'

type Props = {
  block: ResourceAvailability
  showDuration?: boolean
}

const standardDateFormatOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', hourCycle: 'h12', minute: '2-digit' }

const StyledButton = styled(Button)`
  &[data-selected='true'] {
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.common.white};

    & * [x-duration] {
      color: ${theme.palette.grey[200]};
    }
  }
`

const Check = styled(CheckIcon)`
  justify-self: start;
  margin-right: 0.5rem;
  left: 0rem;
  opacity: 0;
  transform: scale(0%);
  transition: 200ms transform ease-in;

  &[data-show='true'] {
    left: 0.25rem;
    opacity: 1;
    transform: scale(100%);
  }
`

const Container = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-auto-flow: column;
  width: 100%;
`

const Duration = styled('div')`
  align-items: center;
  color: ${theme.palette.grey[600]};
  display: grid;
  justify-content: end;
  font-size: 0.75rem;
  gap: 0.125rem;
  grid-auto-flow: column;
  opacity: 0.7;
`

const DurationIcon = styled(TimerIcon)`
  font-size: 1rem;
`

const TimeBlock = ({ block, showDuration }: Props) => {
  const { hourCycle } = useLocale()
  const { date_end, date_start, resource_ids } = block
  const blockDurationInMinutes = differenceInMinutes(date_end, date_start)
  const isAnyResourceSelected = useIsAnyResource()

  const [customer_timezone, durationInMinutes, fitter_timezone, start_date] = useWatch<AppointmentFormData>({
    name: ['customer_timezone', 'duration_in_minutes', 'fitter_timezone', 'start_date'],
  })

  const { setValue } = useFormContext<AppointmentFormData>()

  const { formatDate: formatCustomerDate } = useFormat({
    dateOptions: { ...standardDateFormatOptions, hourCycle, timeZone: customer_timezone as string },
  })

  const { formatDate: formatFitterDate } = useFormat({
    dateOptions: { ...standardDateFormatOptions, hourCycle, timeZone: fitter_timezone as string },
  })

  const customerDisplayDate = formatCustomerDate(date_start)
  const fitterDisplayDate = formatFitterDate(date_start)
  const displayDate = customerDisplayDate === fitterDisplayDate ? customerDisplayDate : `${customerDisplayDate} / ${fitterDisplayDate}`
  const isSelected = isEqual(date_start, start_date as Date) && isEqual(date_end, addMinutes(start_date, durationInMinutes))

  const onClick = useCallback(() => {
    if (isSelected) {
      setValue('clientStartTime', null)
      setValue('start_date', null)
      setValue('resource_ids', [])

      return
    }

    const startTimeWithHourMinutes = setMinutes(setHours(startOfDay(date_start), date_start.getHours()), date_start.getMinutes())
    const newClientStartTime = utcToZonedTime(startTimeWithHourMinutes, customer_timezone as string)

    setValue('clientStartTime', newClientStartTime)
    setValue('duration_in_minutes', blockDurationInMinutes)
    setValue('start_date', date_start)
    // When "Anyone" is selected, set resource_ids to ['*'] so the actual resource is picked from eligibleResourceIds on submit
    setValue('resource_ids', isAnyResourceSelected ? [ANY_RESOURCE_ID] : resource_ids)
  }, [date_start, blockDurationInMinutes, isAnyResourceSelected, isSelected, setValue])

  return (
    <li>
      <StyledButton data-date={date_start.toISOString()} data-selected={isSelected} onClick={onClick} fullWidth variant="outlined">
        <Container>
          {isSelected ? <Check data-show /> : <span />}
          <Typography>{displayDate}</Typography>
          {showDuration ? (
            <Duration title={`This appointment will be ${blockDurationInMinutes} minutes`} x-duration="">
              <DurationIcon />
              <span>{blockDurationInMinutes}&apos;</span>
            </Duration>
          ) : (
            <span />
          )}
        </Container>
      </StyledButton>
    </li>
  )
}

export { TimeBlock }
